<template>
  <div class="page-container daily-tasks">
    <div class="card">
      <div class="tabs-header-wrapper">
        <el-tabs
          v-model="query.scope"
          class="main-tabs"
          @tab-click="search"
        >
          <el-tab-pane
            label="待执行"
            name="todo"
          />
          <el-tab-pane
            label="已完成"
            name="completed"
          />
        </el-tabs>
        <div class="action-btn-group">
          <el-button
            type="primary"
            size="small"
            icon="el-icon-plus"
            @click="openEdit(null)"
          >
            新增任务
          </el-button>
        </div>
      </div>
      <div class="search-bar">
        <el-input
          v-model="query.keyword"
          clearable
          placeholder="任务名称 / 工作内容 / 位置"
          @keyup.enter.native="search"
        />
        <AreaCascader
          v-model="query.areaId"
          :options="options.zones"
          clearable
          filterable
          placeholder="全部区域"
        />
        <el-select
          v-model="query.workType"
          clearable
          placeholder="工作类型"
        >
          <el-option
            v-for="t in workTypes"
            :key="t.value"
            :value="t.value"
            :label="t.label"
          />
        </el-select>
        <el-select
          v-model="query.status"
          clearable
          placeholder="任务状态"
        >
          <el-option
            label="待执行"
            value="pending"
          />
          <el-option
            label="已完成"
            value="completed"
          />
        </el-select>
        <el-input
          v-model="query.userId"
          clearable
          placeholder="执行或受派人员账号"
          @keyup.enter.native="search"
        />
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          value-format="yyyy-MM-dd"
          :start-placeholder="query.scope === 'completed' ? '完成时间起' : '创建时间起'"
          :end-placeholder="query.scope === 'completed' ? '完成时间止' : '创建时间止'"
          @change="search"
        />
        <el-button
          type="primary"
          size="small"
          icon="el-icon-search"
          @click="search"
        >
          搜索
        </el-button>
        <el-button
          size="small"
          icon="el-icon-refresh"
          @click="reset"
        >
          重置
        </el-button>
      </div>
      <div class="table-wrapper">
        <el-table
          v-loading="loading"
          :data="rows"
          row-key="uuid"
          height="100%"
          border
          stripe
          :header-cell-style="{ textAlign: 'center' }"
          :cell-style="{ textAlign: 'center' }"
          :row-class-name="rowClassName"
        >
          <el-table-column
            label="任务名称"
            min-width="180"
            show-overflow-tooltip
          >
            <template slot-scope="scope">
              {{ scope.row.taskName || scope.row.workContent || '-' }}
            </template>
          </el-table-column>
          <el-table-column
            prop="workContent"
            label="工作内容"
            min-width="230"
            show-overflow-tooltip
          />
          <el-table-column
            label="类型"
            width="120"
          >
            <template slot-scope="s">
              <el-tag
                v-if="s.row.workType === 'zyrw'"
                type="danger"
                size="small"
              >
                重要任务
              </el-tag>
              <span v-else>{{ s.row.workTypeName || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="areaPath"
            label="所属区域"
            min-width="200"
            show-overflow-tooltip
          />
          <el-table-column
            prop="workLocation"
            label="具体位置"
            min-width="160"
            show-overflow-tooltip
          />
          <el-table-column
            label="指派人员"
            min-width="150"
          >
            <template slot-scope="s">
              {{ s.row.assignUserNames || s.row.assignTeamName }}
            </template>
          </el-table-column>
          <el-table-column
            label="状态"
            width="90"
          >
            <template slot-scope="s">
              <el-tag
                :type="getStatusType(s.row.status)"
                size="small"
              >
                {{ s.row.statusName || s.row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            label="是否有异常"
            width="80"
          >
            <template slot-scope="s">
              <el-tag
                v-if="s.row.hasFault === 1 || s.row.hasFault === 0"
                :type="s.row.hasFault === 1 ? 'danger' : 'success'"
                size="small"
              >
                {{ s.row.hasFault === 1 ? '是' : '否' }}
              </el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="query.scope === 'completed' ? '完成时间' : '截止时间'"
            width="140"
          >
            <template slot-scope="s">
              {{ query.scope === 'completed' ? formatTime(s.row.executeTime) : formatDeadline(s.row.deadlineDate) }}
            </template>
          </el-table-column>
          <el-table-column
            label="操作"
            width="220"
            fixed="right"
          >
            <template slot-scope="s">
              <el-button
                plain
                size="mini"
                @click="openDetail(s.row.uuid)"
              >
                详情
              </el-button>
              <el-button
                v-if="s.row.canEdit"
                type="primary"
                plain
                size="mini"
                @click="openEdit(s.row)"
              >
                编辑
              </el-button>
              <el-button
                v-if="s.row.canDelete"
                type="danger"
                plain
                size="mini"
                @click="deleteTask(s.row)"
              >
                删除
              </el-button>
              <el-button
                v-if="s.row.canExecute"
                type="success"
                plain
                size="mini"
                @click="openExecute(s.row.uuid)"
              >
                处理任务
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <el-pagination
        class="pagination"
        :current-page.sync="query.pageNo"
        :page-size="query.pageSize"
        :total="total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="load"
      />
    </div>
    <DailyTaskEditDialog
      v-if="editVisible"
      :visible.sync="editVisible"
      :record="selected"
      :options="options"
      @success="load"
    />
    <DailyTaskDetailDialog
      v-if="detailVisible"
      :visible.sync="detailVisible"
      :task-id="detailId"
    />
    <DailyTaskExecuteDialog
      v-if="executeVisible"
      :visible.sync="executeVisible"
      :task-id="executeId"
      @success="load"
    />
  </div>
</template>
<script>
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';
import { areaService } from '@/features/gnmk/area/area.service';
import { dailyTaskService, unwrap, workTypes } from './daily-task.service';
import { formatDate, formatDateTime } from '@/features/common/utils/dateTimeUtil';
import DailyTaskEditDialog from './DailyTaskEditDialog.vue';
import DailyTaskExecuteDialog from './DailyTaskExecuteDialog.vue';
import DailyTaskDetailDialog from './DailyTaskDetailDialog.vue';
export default {
  name: 'DailyTaskPage',
  components: { DailyTaskEditDialog, DailyTaskDetailDialog, DailyTaskExecuteDialog, AreaCascader },
  data () {
    return {
      query: {
        scope: 'todo',
        pageNo: 1,
        pageSize: 20,
        keyword: '',
        areaId: '',
        workType: '',
        status: '',
        userId: '',
        startDate: '',
        endDate: ''
      },
      rows: [],
      total: 0,
      options: { zones: [] },
      workTypes,
      loading: false,
      loadVersion: 0,
      editVisible: false,
      detailVisible: false,
      executeVisible: false,
      selected: null,
      detailId: '',
      executeId: ''
    };
  },
  computed: {
    dateRange: {
      get () {
        return this.query.startDate && this.query.endDate ? [this.query.startDate, this.query.endDate] : null;
      },
      set (value) {
        this.query.startDate = value ? value[0] : '';
        this.query.endDate = value ? value[1] : '';
      }
    }
  },
  methods: {
    getStatusType (status) {
      return { pending: 'warning', completed: 'success' }[status] || '';
    },
    rowClassName ({ row }) {
      return row.workType === 'zyrw' ? 'daily-task-important-row' : '';
    },
    handleSizeChange (size) {
      this.query.pageSize = size;
      this.search();
    },
    formatTime: formatDateTime,
    formatDeadline: formatDate,
    async load () {
      // 后发的筛选请求优先，忽略较早请求的迟到结果。
      const requestVersion = ++this.loadVersion;
      this.loading = true;
      try {
        const page = unwrap(await dailyTaskService.page({ ...this.query }));
        if (requestVersion !== this.loadVersion) {
          return;
        }
        this.rows = page.rows;
        this.total = page.total;
      } catch (e) {
        if (requestVersion === this.loadVersion) {
          this.$message.error('任务查询失败');
        }
      } finally {
        if (requestVersion === this.loadVersion) {
          this.loading = false;
        }
      }
    },
    search () {
      if (this.query.startDate && this.query.endDate && this.query.startDate > this.query.endDate) {
        this.$message.warning('开始日期不能晚于结束日期');
        return;
      }
      this.query.pageNo = 1;
      this.load();
    },
    reset () {
      Object.assign(this.query, {
        keyword: '',
        areaId: '',
        workType: '',
        status: '',
        userId: '',
        startDate: '',
        endDate: ''
      });
      this.search();
    },
    openEdit (record) {
      this.selected = record;
      this.editVisible = true;
    },
    openDetail (id) {
      this.detailId = id;
      this.detailVisible = true;
    },
    openExecute (id) {
      this.executeId = id;
      this.executeVisible = true;
    },
    async deleteTask (row) {
      try {
        await this.$confirm('删除后将无法恢复，确定删除此任务？', '删除任务', { type: 'warning' });
        await dailyTaskService.delete(row.uuid, row.version);
        this.$message.success('任务已删除');
        await this.load();
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') {
          this.$message.error('删除失败，请刷新后重试');
        }
      }
    }
  },
  watch: {
    '$route.query.id' (id) {
      if (id) {
        this.openDetail(id);
      }
    }
  },
  async created () {
    try {
      const areas = unwrap(await areaService.getAreaTree());
      this.options = { zones: Array.isArray(areas) ? areas : [] };
      await this.load();
      if (this.$route.query.id) {
        this.openDetail(this.$route.query.id);
      }
    } catch (e) {
      this.$message.error('日常任务加载失败');
    }
  }
};
</script>
<style lang="scss" scoped>
.daily-tasks {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.card {
  flex: 1;
  min-height: 0;
  padding: 10px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}
.tabs-header-wrapper {
  position: relative;
  flex-shrink: 0;
  .main-tabs { padding-right: 130px; }
  ::v-deep .el-tabs__header { margin-bottom: 10px; }
  ::v-deep .el-tabs__nav-wrap::after { height: 1px; background: #ebeef5; }
  ::v-deep .el-tabs__item { font-size: 15px; font-weight: 500; height: 45px; line-height: 45px; }
}
.action-btn-group {
  position: absolute;
  right: 0;
  top: 8px;
  display: flex;
  gap: 10px;
  z-index: 1;
}
.search-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  .el-input { width: 180px; }
  .el-select { width: 130px; }
  .el-date-editor { width: 260px; }
  .el-button + .el-button { margin-left: 0; }
}
.table-wrapper { flex: 1; min-height: 0; overflow: hidden; }
.pagination { flex-shrink: 0; margin-top: 20px; text-align: right; }
::v-deep .daily-task-important-row td:first-child { border-left: 3px solid #f56c6c; }
</style>
